import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import {
  FORM_LIMITS,
  isAnswerable,
  isRequired,
  stoppedBy,
  stopsOn,
  textIn,
  validateAnswer,
  validateSubmission,
  visibleQuestionIds,
  type AnswerError,
  type Answers,
  type FormSchema,
  type Lang,
  type Question,
  type StopHit,
} from '../logic/form-logic';
import { FORM_COPY, type FormCopy } from './copy';
import { QuestionView } from './QuestionView';
import { ReviewList } from './ReviewList';
import { StopDialog } from './StopDialog';
import { FormButton } from './ui';

/** Answers as the screens hold them: a number may still be half typed. */
export type DraftAnswers = Record<string, unknown>;

const REVIEW = '__review__';

function present(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

function missing(q: Question): AnswerError {
  if (q.type === 'consent') return 'must_agree';
  if (q.type === 'signature') return 'signature_missing';
  return 'required';
}

/** Why this screen cannot be left with Next, or null. */
function stepError(q: Question, value: unknown, today: string): AnswerError | null {
  if (!isAnswerable(q)) return null;
  if (!present(value)) return isRequired(q) ? missing(q) : null;
  return validateAnswer(q, value, { today });
}

/** The error in words, with the limits it is about. */
export function errorText(q: Question, error: AnswerError, copy: FormCopy): string {
  if (q.type === 'number' && error === 'below_min' && typeof q.min === 'number') {
    return copy.belowMin(String(q.min));
  }
  if (q.type === 'number' && error === 'above_max') {
    return copy.aboveMax(String(typeof q.max === 'number' ? q.max : FORM_LIMITS.numberAbs));
  }
  return copy.errors[error];
}

/** Where a returning patient picks up: the first visible question not answered yet. */
export function firstUnanswered(schema: FormSchema, answers: DraftAnswers): string | undefined {
  const byId = new Map(schema.questions.map((q) => [q.id, q]));
  return visibleQuestionIds(schema, answers as Answers).find((id) => {
    const q = byId.get(id);
    return q !== undefined && isAnswerable(q) && !present(answers[id]);
  });
}

/** The stop rule of this question as a hit, when it has one. */
function stopOf(q: Question): StopHit | null {
  if ((q.type === 'yes_no' || q.type === 'single_choice' || q.type === 'multi_choice') && q.stop) {
    return { questionId: q.id, message: q.stop.message };
  }
  return null;
}

/**
 * What an answer that stops an online booking does (SFM-D27).
 * - `after` (links, the counter, posters): nothing while answering; the page says so after sending.
 * - `block` (booking online, the builder's preview): "Unable to continue" with the pharmacy's words,
 *   and no way on until the answer changes.
 */
export type StopBehaviour =
  | { mode: 'after' }
  | {
      mode: 'block';
      /** The pharmacy's number, offered in the dialog. */
      phone?: string | null;
      /** A second way out of the dialog, e.g. "Choose another service". */
      onLeave?: () => void;
      leaveLabel?: string;
    };

export interface FormRunnerProps {
  /** The patient's view of the form (`patientView`): no points, no bands. */
  schema: FormSchema;
  /** The form's language: its questions and answer choices. */
  lang: Lang;
  answers: DraftAnswers;
  onAnswersChange: (answers: DraftAnswers) => void;
  /** Each Next and Back, with the answers so far: where a draft is saved (SFM-D10). */
  onStep?: (answers: DraftAnswers) => void;
  /** The checked answers. Throw to say they did not go. Absent in a preview. */
  onSubmit?: (answers: Answers) => Promise<void>;
  /** `YYYY-MM-DD` in the pharmacy's time zone, for date questions. */
  today: string;
  /** The desktop's live preview: says so, and sends nothing. */
  preview?: boolean;
  /** A question to open on (resuming a draft); the first one otherwise. */
  startAt?: string;
  /** The runner's own words (buttons, progress, problems), when the page's differ (SFM-D33). */
  copyLang?: Lang;
  /** False inside the booking, whose own heading is the form's title. */
  showTitle?: boolean;
  /** Open on "Check your answers" (coming back to a form already answered). */
  startAtReview?: boolean;
  /** Problems found after the fact (the server's), shown on "Check your answers". */
  initialErrors?: Record<string, AnswerError>;
  /** Back on the first question leaves the form (the booking's previous step). */
  onExit?: () => void;
  /** The last button's words, e.g. "Continue" in the booking instead of "Send my answers". */
  finishLabel?: string;
  /** "Skip this form for now": an optional form inside the booking. */
  onSkip?: () => void;
  stop?: StopBehaviour;
}

/**
 * The patient's form, one question per screen (SFM-D1, WCAG 2.1 AA).
 *
 * - The question is the screen's heading, and focus moves to it on every step,
 *   so a screen reader says where the patient is.
 * - Progress is written out ("Question 3 of 9") in a status region.
 * - It never moves on by itself: choosing an answer is not the same as being
 *   done with the question (and arrowing through radio buttons must not jump screens).
 * - A problem is announced at the top of the screen and tied to its field.
 * - Before anything is sent the patient checks every answer and can change any.
 * - Booking online (`stop` block), an answer that stops the booking says so on Next,
 *   and again at the end as a backstop.
 */
export function FormRunner({
  schema,
  lang,
  answers,
  onAnswersChange,
  onStep,
  onSubmit,
  today,
  preview = false,
  startAt,
  copyLang,
  showTitle = true,
  startAtReview = false,
  initialErrors,
  onExit,
  finishLabel,
  onSkip,
  stop = { mode: 'after' },
}: FormRunnerProps) {
  const pageLang = copyLang ?? lang;
  // The runner's words follow the page; the form's own words, and its answer choices, the form.
  const copy = FORM_COPY[pageLang];
  const words = FORM_COPY[lang];
  const idPrefix = useId().replace(/:/g, '');
  const byId = useMemo(() => new Map(schema.questions.map((q) => [q.id, q])), [schema]);
  const visible = visibleQuestionIds(schema, answers as Answers);
  const hasInitialErrors = initialErrors !== undefined && Object.keys(initialErrors).length > 0;

  const [current, setCurrent] = useState<string>(
    startAtReview ? REVIEW : startAt && visible.includes(startAt) ? startAt : (visible[0] ?? REVIEW),
  );
  // Reached from "Check your answers" with Change: Next goes back there.
  const [returnToReview, setReturnToReview] = useState(false);
  const [error, setError] = useState<{ id: string; code: AnswerError } | null>(null);
  const [reviewErrors, setReviewErrors] = useState<Record<string, AnswerError> | null>(
    hasInitialErrors ? initialErrors : null,
  );
  const [sending, setSending] = useState(false);
  const [sendFailed, setSendFailed] = useState(false);
  const [previewSent, setPreviewSent] = useState(false);
  const [stopped, setStopped] = useState<StopHit | null>(null);

  const headingRef = useRef<HTMLHeadingElement>(null);
  const summaryRef = useRef<HTMLDivElement>(null);
  const questionRef = useRef<HTMLDivElement>(null);
  const [focusTick, setFocusTick] = useState(0);
  // Problems handed in from outside are announced as soon as they are shown.
  const [summaryTick, setSummaryTick] = useState(hasInitialErrors ? 1 : 0);
  const [answerTick, setAnswerTick] = useState(0);
  useEffect(() => {
    if (focusTick > 0) headingRef.current?.focus();
  }, [focusTick]);
  useEffect(() => {
    if (summaryTick > 0) summaryRef.current?.focus();
  }, [summaryTick]);
  // While "Unable to continue" is open, the question behind it cannot be reached. Declared
  // before the effect below, so the question is reachable again before focus goes back to it.
  useEffect(() => {
    const block = questionRef.current;
    if (!block) return;
    if (stopped) block.setAttribute('inert', '');
    else block.removeAttribute('inert');
  }, [stopped]);
  // Closing it returns to the answer that stopped it: changing it is the next thing to do.
  useEffect(() => {
    if (answerTick === 0) return;
    const chosen = questionRef.current?.querySelector<HTMLInputElement>('input:checked');
    (chosen ?? headingRef.current)?.focus();
  }, [answerTick]);

  // A question hidden by a changed answer is never shown: the nearest one is.
  const step =
    current === REVIEW || visible.includes(current)
      ? current
      : (visible.find((id) => schema.questions.findIndex((q) => q.id === id) >
          schema.questions.findIndex((q) => q.id === current)) ?? REVIEW);

  const go = (to: string) => {
    setError(null);
    setCurrent(to);
    setFocusTick((n) => n + 1);
  };

  const next = () => {
    const q = byId.get(step);
    if (!q) return;
    const problem = stepError(q, answers[q.id], today);
    if (problem) {
      setError({ id: q.id, code: problem });
      setSummaryTick((n) => n + 1);
      return;
    }
    if (stop.mode === 'block' && stopsOn(q, answers[q.id])) {
      setStopped(stopOf(q));
      return;
    }
    onStep?.(answers);
    if (returnToReview) {
      setReturnToReview(false);
      go(REVIEW);
      return;
    }
    const index = visible.indexOf(step);
    go(visible[index + 1] ?? REVIEW);
  };

  const back = () => {
    onStep?.(answers);
    setReturnToReview(false);
    if (step === REVIEW) {
      if (visible.length > 0) go(visible[visible.length - 1]!);
      else onExit?.();
      return;
    }
    const index = visible.indexOf(step);
    if (index > 0) go(visible[index - 1]!);
    else onExit?.();
  };

  const send = async () => {
    setSendFailed(false);
    const check = validateSubmission(schema, answers, { today });
    if (!check.ok) {
      setReviewErrors(check.errors);
      setSummaryTick((n) => n + 1);
      return;
    }
    setReviewErrors(null);
    if (stop.mode === 'block') {
      // A backstop: Next already said so, unless the answer arrived some other way.
      const hit = stoppedBy(schema, check.answers);
      if (hit) {
        setError(null);
        setCurrent(hit.questionId);
        setStopped(hit);
        return;
      }
    }
    if (preview || !onSubmit) {
      setPreviewSent(true);
      return;
    }
    setSending(true);
    try {
      await onSubmit(check.answers);
    } catch {
      setSendFailed(true);
      setSummaryTick((n) => n + 1);
    } finally {
      setSending(false);
    }
  };

  const closeStop = () => {
    setStopped(null);
    setAnswerTick((n) => n + 1);
  };

  const setAnswer = (id: string, value: unknown) => {
    const nextAnswers = { ...answers };
    if (value === undefined) delete nextAnswers[id];
    else nextAnswers[id] = value;
    if (error?.id === id) setError(null);
    if (reviewErrors?.[id]) {
      const rest = { ...reviewErrors };
      delete rest[id];
      setReviewErrors(rest);
    }
    onAnswersChange(nextAnswers);
  };

  const position = step === REVIEW ? visible.length : visible.indexOf(step) + 1;
  const total = visible.length;
  const percent = total === 0 ? 100 : Math.round((step === REVIEW ? 1 : (position - 1) / total) * 100);
  const question = step === REVIEW ? null : byId.get(step);
  const errorMessage = question && error?.id === question.id ? errorText(question, error.code, copy) : null;
  const dialog = question && stopped?.questionId === question.id ? stopped : null;
  const twoLanguages = pageLang !== lang;

  const reviewMessages: Record<string, string> = {};
  for (const [id, code] of Object.entries(reviewErrors ?? {})) {
    const q = byId.get(id);
    if (q) reviewMessages[id] = errorText(q, code, copy);
  }

  const skip = onSkip ? (
    <p className="text-center sm:text-right">
      <FormButton variant="link" onClick={onSkip}>
        {copy.skip}
      </FormButton>
    </p>
  ) : null;

  return (
    <div lang={pageLang} className="space-y-6 text-foreground">
      {preview && (
        <p className="rounded-xl border border-border bg-muted px-4 py-3 text-base text-muted-foreground">
          {copy.previewNotice}
        </p>
      )}
      <div>
        {showTitle && (
          <h1 lang={twoLanguages ? lang : undefined} className="text-base font-medium text-muted-foreground">
            {textIn(schema.title, lang)}
          </h1>
        )}
        <div
          aria-hidden="true"
          className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted"
        >
          <div className="h-full rounded-full bg-primary" style={{ width: `${percent}%` }} />
        </div>
        <p role="status" className="mt-2 text-base text-muted-foreground">
          {step === REVIEW ? copy.reviewTitle : copy.questionOf(position, total)}
        </p>
      </div>

      {question && errorMessage && (
        <div
          ref={summaryRef}
          tabIndex={-1}
          role="alert"
          className="rounded-xl border border-danger/40 bg-danger/5 p-4 focus:outline-none"
        >
          <p className="flex items-center gap-2 text-lg font-semibold text-foreground">
            <AlertCircle className="h-5 w-5 shrink-0 text-danger" aria-hidden="true" />
            {copy.problemTitle}
          </p>
          <p className="mt-1 text-base">
            <a href={`#${idPrefix}-${question.id}`} className="text-danger underline underline-offset-4">
              {errorMessage}
            </a>
          </p>
        </div>
      )}

      {question ? (
        <>
          <div
            ref={questionRef}
            lang={twoLanguages ? lang : undefined}
            className={dialog ? 'space-y-6 opacity-50' : 'space-y-6'}
          >
            {step === visible[0] && schema.intro && (
              <p className="whitespace-pre-line text-lg leading-relaxed text-foreground">
                {textIn(schema.intro, lang)}
              </p>
            )}
            <QuestionView
              key={question.id}
              idPrefix={idPrefix}
              question={question}
              lang={lang}
              copy={words}
              value={answers[question.id]}
              onChange={(value) => setAnswer(question.id, value)}
              error={errorMessage}
              errorLang={twoLanguages ? pageLang : undefined}
              headingRef={headingRef}
              today={today}
            />
          </div>
          {dialog && stop.mode === 'block' ? (
            <StopDialog
              copy={copy}
              message={textIn(dialog.message, lang)}
              lang={lang}
              phone={stop.phone}
              preview={preview}
              onChange={closeStop}
              onLeave={stop.onLeave}
              leaveLabel={stop.leaveLabel}
            />
          ) : (
            <>
              <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-between">
                {visible.indexOf(step) > 0 || onExit ? (
                  <FormButton variant="outline" onClick={back}>
                    {copy.back}
                  </FormButton>
                ) : (
                  <span />
                )}
                <FormButton onClick={next}>
                  {returnToReview || visible.indexOf(step) === visible.length - 1 ? copy.toReview : copy.next}
                </FormButton>
              </div>
              {skip}
            </>
          )}
        </>
      ) : (
        <div className="space-y-5">
          <div>
            <h2 ref={headingRef} tabIndex={-1} className="text-2xl font-semibold tracking-tight text-heading focus:outline-none">
              {copy.reviewTitle}
            </h2>
            <p className="mt-2 text-lg">{copy.reviewIntro}</p>
          </div>
          {(reviewErrors || sendFailed) && (
            <div
              ref={summaryRef}
              tabIndex={-1}
              role="alert"
              className="rounded-xl border border-danger/40 bg-danger/5 p-4 focus:outline-none"
            >
              <p className="flex items-center gap-2 text-lg font-semibold">
                <AlertCircle className="h-5 w-5 shrink-0 text-danger" aria-hidden="true" />
                {copy.problemTitle}
              </p>
              {sendFailed ? (
                <p className="mt-1 text-base">{copy.sendFailed}</p>
              ) : (
                <>
                  <p className="mt-1 text-base">{copy.fixBeforeSending}</p>
                  <ul className="mt-2 list-disc space-y-1 pl-6">
                    {Object.keys(reviewMessages).map((id) => (
                      <li key={id}>
                        <FormButton
                          variant="link"
                          className="text-left text-danger"
                          onClick={() => {
                            setReturnToReview(true);
                            go(id);
                          }}
                        >
                          <span lang={twoLanguages ? lang : undefined}>{textIn(byId.get(id)!.label, lang)}</span>
                          {`: ${reviewMessages[id]}`}
                        </FormButton>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
          <ReviewList
            idPrefix={idPrefix}
            schema={schema}
            answers={answers}
            visible={visible}
            lang={lang}
            copy={copy}
            errors={reviewMessages}
            onChange={(id) => {
              setReturnToReview(true);
              go(id);
            }}
          />
          {previewSent && (
            <p role="status" className="rounded-xl border border-border bg-muted px-4 py-3 text-base">
              {copy.previewSent}
            </p>
          )}
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
            {visible.length > 0 || onExit ? (
              <FormButton variant="outline" onClick={back}>
                {copy.back}
              </FormButton>
            ) : (
              <span />
            )}
            <FormButton onClick={() => void send()} disabled={sending} aria-busy={sending || undefined}>
              {sending ? (finishLabel ? copy.working : copy.sending) : (finishLabel ?? copy.send)}
            </FormButton>
          </div>
          {skip}
        </div>
      )}
    </div>
  );
}
